import BaseExtension from './base';

import { createElement, removeClass, addClass, isFunction, existy, hasClass, each } from '../utils';

import createSvg, { PAUSE_CIRCLE, PLAY_CIRCLE, MUTED, VOLUME_UP, VOLUME_DOWN, BACKWARD, FORWARD, PLAY } from '../svgs';

const CN = 'rmp-ext-placeholder';
const ANIMATION_CN = `${CN}--animation`;

const ICON_CN = `${CN}__icon`;
const PLAY_BUTTON_CN = `${CN}__play-btn`;

const SPACE_CODE  = 32;
const LEFT_CODE   = 37;
const UP_CODE     = 38;
const RIGHT_CODE  = 39;
const DOWN_CODE   = 40;
const PREV_FRAME  = 74;
const NEXT_FRAME  = 75;

const VOLUME_DELTA = 0.1;
const MAX_VOLUME = 1;
const MIN_VOLUME = 0;

const MAP = {};

MAP[SPACE_CODE]   = '_togglePlay';
MAP[LEFT_CODE]    = '_skeepLeft';
MAP[RIGHT_CODE]   = '_skeepRight';
MAP[UP_CODE]      = '_upVolume';
MAP[DOWN_CODE]    = '_downVolume';
MAP[PREV_FRAME]   = '_prevFrame';
MAP[NEXT_FRAME]   = '_nextFrame';

const CLICK_DELAY = 350;

let timerId;
let animationTimerId;
let accessibilityEnabled;

export default class PlaceholderExtension extends BaseExtension {
   /** @type {HTMLElement} */
   $outer;
   /** @type {HTMLElement} */
   $placeholderIcon;
   /** @type {HTMLElement} */
   $playButton;

   constructor(player, state, options) {
      super(player, state, options);
      this.logger.debug('placeholder:extension:creating');

      accessibilityEnabled = this.options.accessibility;

      this.render();
      if (options.isHandlingKeyboardEvents) {
         this.addEvent(document, 'keydown', this.onKeyDown.bind(this), options.globalKeyboardListeners);
      }

      this.addEvent(this.$outer, 'click', this.onPlaceholderClick.bind(this));
      this.addEvent(this.$playButton, 'click', this.onPlaceholderClick.bind(this));
      setTimeout(() => {
         const $title = this.$outer.parentNode.querySelector('.rmp-ext-title');
         if ($title) {
            this.addEvent($title, 'click', this.onPlaceholderClick.bind(this));
         }
         this.addEvent(this.$outer.parentNode, 'dblclick', this.toggleFullScreen.bind(this));
      });

      this.logger.debug('placeholder:extension:created');
   }

   render() {
      this.$outer = createElement('div', { class: CN, tabindex: -1 });
      this.$placeholderIcon = createElement('div', { class: ICON_CN, tabindex: -1 }, this.$outer);
      this.$playButton = createElement('button', { type: 'button', class: PLAY_BUTTON_CN, tabindex: -1 }, this.$outer);
      createSvg(this.$playButton, PLAY);
      this.$playButton.setAttribute('aria-label', 'Play');
   }

   onPlaceholderClick() {
      this.logger.debug('placeholder:click');
      clearTimeout(timerId);
      timerId = setTimeout(() => this._togglePlay(), CLICK_DELAY);
   }

   onKeyDown(e) {
      if (!this.player.getElement()) return;

      this.logger.debug('placeholder:keydown');
      const target = e.target || e.srcElement;
      let isPlayerEvent = target === this.player.getElement();
      isPlayerEvent = isPlayerEvent || hasClass(target, 'rplayer');
      // ie 11
      isPlayerEvent = isPlayerEvent || this.player.getElement().parentNode.contains(target);

      if ( isPlayerEvent ) {
         this._processKeyDown(e);
      } else if ( this.options.globalKeyboardListeners && !_isTextInput(e.target)) {
         this._processKeyDown(e);
      }
   }

   _processKeyDown(e) {
      if (e.target instanceof HTMLInputElement) {
         return;
      }

      const keyCode = e.keyCode;

      // Accessibility: If accessibility enabled, space button should trigger focused one, not play
      if (keyCode === SPACE_CODE && accessibilityEnabled && e.target.tagName === 'BUTTON') {
         return;
      }

      let fnc = MAP[keyCode];
      if ( existy(fnc) && isFunction(this[fnc]) ) {
         this[fnc](this.player);
         e.preventDefault();
         e.stopPropagation();
      }

      fnc = this.options.keyboardListeners[keyCode];
      if ( existy(fnc) && isFunction(fnc) ) {
         fnc(this.player);
         e.preventDefault();
         e.stopPropagation();
      }
   }

   toggleFullScreen() {
      this.logger.debug('placeholder:toggleFullScreen');
      clearTimeout(timerId);
      if (this.options.showFullScreen) {
         this.bus.emit('fullscreenchanging');
      }
   }

   _togglePlay() {
      this.logger.debug('placeholder:togglePlay');
      if ( this.options.isMobile ) {
         this.bus.emit('mobiletouch');
      } else if (this.player.isPaused()) {
         this.player.play();
         this._showAction(PLAY_CIRCLE);
      } else {
         this.player.pause();
         this._showAction(PAUSE_CIRCLE);
      }
   }

   _skeepLeft() {
      this.logger.debug('placeholder:skeepLeft');
      this.player.setCurrentTime(this.player.getCurrentTime() - this.options.skipDelta);
      this._showAction(BACKWARD);
   }

   _skeepRight() {
      this.logger.debug('placeholder:skeepRight');
      this.player.setCurrentTime(this.player.getCurrentTime() + this.options.skipDelta);
      this._showAction(FORWARD);
   }

   _upVolume() {
      this.logger.debug('placeholder:volumeup');
      const volume = Math.min(MAX_VOLUME, VOLUME_DELTA + this.player.getVolume());
      this.player.setMuted(false);
      this.player.setVolume(volume);
      this._showAction(VOLUME_UP);
   }

   _downVolume() {
      this.logger.debug('placeholder:volumedown');
      const volume = Math.max(MIN_VOLUME, this.player.getVolume() - VOLUME_DELTA).toFixed(2);
      // eslint-disable-next-line eqeqeq
      if (volume == MIN_VOLUME) {
         this.player.setVolume(0);
         this.player.setMuted(true);
         this._showAction(MUTED);
      } else {
         this.player.setVolume(volume);
         this._showAction(VOLUME_DOWN);
      }
   }

   _showAction(path) {
      this.logger.debug('placeholder:showaction');
      removeClass(this.$outer, ANIMATION_CN);
      clearTimeout(animationTimerId);
      animationTimerId = setTimeout(() => {
         this.logger.debug('placeholder:showaction:clear');
         this._clearPlaceholderIcon();
         createSvg(this.$placeholderIcon, path);
         addClass(this.$outer, ANIMATION_CN);
         animationTimerId = setTimeout(() => {
            this.logger.debug('placeholder:showaction:remove');
            this._clearPlaceholderIcon();
            removeClass(this.$outer, ANIMATION_CN);
         }, 500);
      }, CLICK_DELAY);
   }

   _clearPlaceholderIcon() {
      if (!this.$placeholderIcon.childNodes.length) {
         return;
      }
      each(this.$placeholderIcon.childNodes, c => {
         this.$placeholderIcon.removeChild(c);
      });
   }

   _prevFrame() {
      this.player.prevFrame();
   }

   _nextFrame() {
      this.player.nextFrame();
   }

   getOuter() {
      return this.$outer;
   }

   destroy() {
      super.destroy();
      this.$outer.removeChild(this.$placeholderIcon);
      this.$outer.parentNode.removeChild(this.$outer);

      this.$outer = null;
      this.$placeholderIcon = null;
   }
}

function _isTextInput(target) {
   let res = false;

   res = res || target instanceof HTMLTextAreaElement;
   res = res || target instanceof HTMLInputElement && target.type === 'text';

   return res;
}
